import { RowDataPacket } from 'mysql2';
import { parseJsonValue } from '@/lib/json';

export type TopNavItem = {
  label: string;
  href: string;
  icon: string;
  enabled: boolean;
  sort_order: number;
};

export type CategoryDisplayConfig = {
  category_id: string;
  show_in_menu: boolean;
  menu_label: string;
  menu_href: string;
  menu_icon: string;
  sub_items: string[];
  sort_order: number;
  is_featured: boolean;
};

export type MenuConfig = {
  topNav: TopNavItem[];
  quickSearch: string[];
};

export const defaultMenuConfig: MenuConfig = {
  topNav: [
    {
      label: 'Khuyến mãi',
      href: '/khuyen-mai',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_1_icon.png?v=6754',
      enabled: true,
      sort_order: 1,
    },
    {
      label: 'Trả góp',
      href: '/tra-gop',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_2_icon.png?v=6754',
      enabled: true,
      sort_order: 2,
    },
    {
      label: 'Xây dựng cấu hình',
      href: '/xay-dung-cau-hinh',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_4_icon.png?v=6754',
      enabled: true,
      sort_order: 4,
    },
    {
      label: 'Tra cứu bảo hành',
      href: '/tra-cuu-bao-hanh',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_5_icon.png?v=6754',
      enabled: true,
      sort_order: 6,
    },
    {
      label: 'Lịch vệ sinh máy',
      href: '/lien-he',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_4_icon.png?v=6754',
      enabled: true,
      sort_order: 7,
    },
    {
      label: 'Hotline',
      href: '/',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_6_icon.png?v=6754',
      enabled: true,
      sort_order: 8,
    },
  ],

  quickSearch: ['SSD', 'VGA', 'PC gaming', 'cpu AMD', 'RAM', 'Màn hình'],
};

export function createDefaultCategoryDisplayConfig(
  categories: Array<{
    id: string;
    name: string;
    slug: string;
    icon?: string | null;
    image_url?: string | null;
    sort_order?: number;
  }>
): CategoryDisplayConfig[] {
  return categories.map((category, index) => ({
    category_id: category.id,
    show_in_menu: true,
    menu_label: category.name,
    menu_href: `/danh-muc/${category.slug}`,
    menu_icon: category.image_url || category.icon || '',
    sub_items: [],
    sort_order: category.sort_order ?? index + 1,
    is_featured: false,
  }));
}

export function mergeCategoryDisplayConfig(
  categories: Array<{
    id: string;
    name: string;
    slug: string;
    icon?: string | null;
    image_url?: string | null;
    sort_order?: number;
  }>,
  rawValue: unknown
) {
  const rawItems = parseJsonValue<CategoryDisplayConfig[]>(rawValue, []);
  const rawMap = new Map(rawItems.map((item) => [item.category_id, item]));

  return createDefaultCategoryDisplayConfig(categories).map((category) => {
    const current = rawMap.get(category.category_id);
    return {
      ...category,
      ...(current || {}),
      category_id: category.category_id,
      menu_label: current?.menu_label || category.menu_label,
      menu_href: current?.menu_href || category.menu_href,
      menu_icon: current?.menu_icon || category.menu_icon,
      sub_items: Array.isArray(current?.sub_items)
        ? current!.sub_items.filter(Boolean)
        : category.sub_items,
    };
  });
}

export function normalizeMenuConfig(rawValue: unknown): MenuConfig {
  const parsed = parseJsonValue<MenuConfig>(rawValue, defaultMenuConfig);
  const normalizedTopNav = Array.isArray(parsed.topNav) ? parsed.topNav : defaultMenuConfig.topNav;

  const hasMaintenanceTab = normalizedTopNav.some((item) => {
    const label = String(item?.label || '')
      .trim()
      .toLowerCase();
    return label === 'lịch vệ sinh máy' || label === 'lich ve sinh may';
  });

  const hasWarrantyLookupTab = normalizedTopNav.some((item) => {
    const label = String(item?.label || '')
      .trim()
      .toLowerCase();
    return label === 'tra cứu bảo hành' || label === 'tra cuu bao hanh';
  });

  const hotlineIndex = normalizedTopNav.findIndex(
    (item) =>
      String(item?.label || '')
        .trim()
        .toLowerCase() === 'hotline'
  );

  if (!hasWarrantyLookupTab) {
    const warrantyLookupItem = {
      label: 'Tra cứu bảo hành',
      href: '/tra-cuu-bao-hanh',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_5_icon.png?v=6754',
      enabled: true,
      sort_order: 6,
    };

    if (hotlineIndex >= 0) {
      normalizedTopNav.splice(hotlineIndex, 0, warrantyLookupItem);
    } else {
      normalizedTopNav.push(warrantyLookupItem);
    }
  }

  // Recalculate hotlineIndex if we just added warranty lookup
  const updatedHotlineIndex = normalizedTopNav.findIndex(
    (item) =>
      String(item?.label || '')
        .trim()
        .toLowerCase() === 'hotline'
  );

  if (!hasMaintenanceTab) {
    const maintenanceItem = {
      label: 'Lịch vệ sinh máy',
      href: '/lien-he',
      icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_nav_4_icon.png?v=6754',
      enabled: true,
      sort_order: 7,
    };

    if (updatedHotlineIndex >= 0) {
      normalizedTopNav.splice(updatedHotlineIndex, 0, maintenanceItem);
    } else {
      normalizedTopNav.push(maintenanceItem);
    }
  }

  return {
    topNav: normalizedTopNav,
    quickSearch: Array.isArray(parsed.quickSearch)
      ? parsed.quickSearch.filter(Boolean)
      : defaultMenuConfig.quickSearch,
  };
}

export async function getNavigationSettings(
  queryFn: <T extends RowDataPacket[]>(sql: string, params?: unknown[]) => Promise<T>
) {
  const rows = await queryFn<RowDataPacket[]>(
    'SELECT setting_key, setting_value FROM system_settings WHERE setting_key IN (?, ?)',
    ['menu_config', 'category_display_config']
  );
  const settings = rows.reduce<Record<string, unknown>>((acc, row) => {
    acc[row.setting_key] = row.setting_value;
    return acc;
  }, {});
  return settings;
}
